import { useContext, useState } from 'react';

import { Box, Button, Dialog, DialogActions, DialogContent, type DialogProps, DialogTitle, styled } from '@mui/material';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

import { type RosterTableData } from './types';
import { CLASS_STUDENTS } from '../../config/apiPaths';
import { RosterContext } from '../../context/RosterContext';
import { getUrlParams, getUrls } from '../../utils/url';
import { StyledLoadingButton } from '../forms/LoadingButton';

const StyledDialog = styled(Dialog)`
    & .MuiDialog-paper {
        width: 500px;
    }

    & .MuiDialogTitle-root {
        background-color: ${(props) => props.theme.palette.primary.main};
        color: white;
    }

    & .MuiDialogTitle-root + .MuiDialogContent-root {
        padding-top: 16px;
    }
` as typeof Dialog;

export type RosterDeleteModalProps = DialogProps & {
    data: RosterTableData | undefined;
};

const RosterDeleteModal = (props: RosterDeleteModalProps) => {
    const { data, ...other } = props;

    const { selectedStudentId, setOpenDeleteModal, rosterData, setRosterData } = useContext(RosterContext);
    const { api } = getUrls();
    const userUuid = getUrlParams().user_uuid ?? window.localStorage.getItem('user_uuid');
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const { t } = useTranslation('roster');
    const { enqueueSnackbar } = useSnackbar();

    const getStudentIdValue = () => {
        if (data && data.student_id) return data.student_id;
    };

    const getUserNameValue = () => {
        if (data && data.user_name) return data.user_name;
    };

    const handleClose = () => {
        setOpenDeleteModal(false);
    };

    const handleSubmit = async () => {
        setIsLoading(true);
        const studentId = getStudentIdValue();
        try {
            await axios.delete(`${api}${CLASS_STUDENTS}?studentId=${studentId}`, { headers: { token: userUuid } });
            const newRoster = rosterData.roster.slice();
            const removeIndex = newRoster.findIndex((record) => record.student_id === selectedStudentId);
            newRoster.splice(removeIndex, 1);
            setRosterData({ loading: false, roster: newRoster });
            setOpenDeleteModal(false);
        } catch (error) {
            console.error(error);
            enqueueSnackbar(t('alert-content-8', { userName: getUserNameValue() }), { variant: 'error' });
            setOpenDeleteModal(false);
        }
        setIsLoading(false);
    };

    return (
        <StyledDialog {...other}>
            <DialogTitle>{t('Delete')}</DialogTitle>

            <DialogContent>
                {t('confirm-delete-message')}

                <Box>{getUserNameValue()}</Box>
            </DialogContent>

            <DialogActions>
                <Button variant={'outlined'} onClick={handleClose} disabled={isLoading}>
                    {t('Cancel')}
                </Button>
                <StyledLoadingButton variant={'contained'} onClick={handleSubmit} loading={isLoading}>
                    {t('Delete')}
                </StyledLoadingButton>
            </DialogActions>
        </StyledDialog>
    );
};

export default RosterDeleteModal;
